import type { Project, ProjectParams, ThemeData } from '../types';
import type { SeededRandom } from '../rng/SeededRandom';

let projectIdCounter = 0;

export function resetProjectIdCounter(): void {
  projectIdCounter = 0;
}

/** Set counter to max existing ID so new projects get unique IDs (call after load). */
export function syncProjectIdCounter(projects: { id: string }[]): void {
  let max = 0;
  for (const p of projects) {
    const n = parseInt(p.id.replace('project_', ''), 10);
    if (n > max) max = n;
  }
  projectIdCounter = max;
}

function generateProjectName(rng: SeededRandom, themeData: ThemeData): string {
  const pattern = rng.pick(themeData.names.namePatterns);
  const parts = themeData.names.projectNameParts;

  return pattern.replace(/\{(\w+)\}/g, (_match, key: string) => {
    switch (key) {
      case 'prefix':
        return rng.pick(parts.prefixes);
      case 'noun':
        return rng.pick(parts.nouns);
      case 'suffix':
        return rng.pick(parts.suffixes);
      default:
        return key;
    }
  });
}

export function createProject(
  params: ProjectParams,
  themeData: ThemeData,
  rng: SeededRandom,
): Project {
  const totalTicks = themeData.balance.projectTicks[params.size];
  const name = params.name || generateProjectName(rng, themeData);

  // Initialize progress with all stat keys at 0
  const progress: Record<string, number> = {};
  const category = themeData.entities.projectCategories.find((c) => c.id === params.category);
  if (category) {
    for (const stat of Object.keys(category.statWeights)) {
      progress[stat] = 0;
    }
  }

  return {
    id: `project_${++projectIdCounter}`,
    name,
    sequelNumber: params.isSequel ? 1 : 0, // Will be adjusted by sequel detection
    sequelOf: params.sequelOf ?? null,
    category: params.category,
    subcategory: params.subcategory,
    platform: params.platform,
    platforms: params.platforms ?? [params.platform],
    phase: 'planning',
    progress,
    phaseProgress: 0,
    quality: 0,
    bugs: 0,
    assignedTeamId: params.teamId,
    ticksInPhase: 0,
    totalTicks,
    size: params.size,
    marketingStrategyId: params.marketingStrategyId,
    debugCycles: 0,
  };
}

export { generateProjectName };
